// This is a temporary workaround. When the user feature is implemented in the future release (probably in the domain store?), we need to refactor the logic.

import {
  calculateHasSharedCalendar,
  Village
} from "../../shared/interfaces/village";
import {ApiUserRoles} from "./authorisation/constants/ApiRoles";

/**
 * Provides information of the authenticated user.
 */
export class AuthUser {
  private _name = "";
  private _preferredUserName = "";
  private _upn = "";
  private _oid = "";
  private _isAuthorised = false;
  private _villages: Village[] | null = null;
  private _roles: string = "";
  private _permissions: string = "";
  private _villagesByCountry: {[key: string]: Village[]} = {};
  private static _authUser: AuthUser;

  private constructor() {}

  /**
   * The name of the authenticated user.
   */
  get Name(): string {
    return this._name;
  }

  /**
   * The primary user name of the user. It could be an E-mail address, phone number, or a generic username without a specified format.
   */
  get PreferredUserName(): string {
    return this._preferredUserName;
  }

  /**
   * User Principal Name, normally in the E-mail address format.
   */
  get Upn(): string {
    return this._upn;
  }

  /**
   * The immutable identifier of the user's account.
   */
  get Oid(): string {
    return this._oid;
  }

  /**
   * If user has at least 1 Role is authorised, otherwise user is not authorised.
   */
  get IsAuthorised(): boolean {
    return this._isAuthorised;
  }

  /**
   * The villages that the user has access to.
   */
  get Villages(): Village[] | null {
    return this._villages;
  }

  /**
   * The AAD roles of the user.
   */
  get Roles(): string[] | null {
    return this._roles.trim() === "" ? null : this._roles.split(",");
  }

  /**
   * Whether the user is a super admin or office admin of the app.
   */
  get IsSuperUser(): boolean {
    return (
      this._roles.includes(ApiUserRoles.SuperAdmin) ||
      this._roles.includes(ApiUserRoles.Office)
    );
  }

  /**
   * The permissions of the user based on role.
   */
  get Permissions(): string[] {
    return this._permissions.split(",");
  }

  /**
   * Get the instance of AuthUser. Instantiate an instance of AuthUser if it has not been instantiated.
   */
  public static getInstance(): AuthUser {
    if (!this._authUser) {
      this._authUser = new AuthUser();
    }
    return this._authUser;
  }

  get villagesByCountry(): {[key: string]: Village[]} {
    return this._villagesByCountry;
  }

  /**
   * Set the details of AuthUser.
   */
  public setAuthUser(userData: any, villages: any): void {
    const getValue = (key: string): string => {
      return userData.find((o: any) => o.key === key).value;
    };

    if (userData) {
      this._name = getValue("name");
      this._preferredUserName = getValue("preferred_username");
      this._oid = getValue("uid");
      this._upn = getValue(
        "http://schemas.xmlsoap.org/ws/2005/05/identity/claims/upn"
      );
      this._isAuthorised = getValue("isAuthorised").toLowerCase() === "true";
      this._roles = getValue(
        "http://schemas.microsoft.com/ws/2008/06/identity/claims/role"
      );
      this._permissions = getValue("permissions");
    }

    if (villages) {
      this._villages = villages
        .map((v: any) => {
          if (v.villageCalendars && v.villageCalendars.length > 0) {
            const village: Village = {
              id: v.id,
              name: v.name,
              timeZone: v.timeZone,
              code: v.code,
              country: v.country,
              calendars: v.villageCalendars,
              hasSharedCalendar: calculateHasSharedCalendar(
                v.villageCalendars
              )
            };
            if (this._villagesByCountry[v.country]) {
              this._villagesByCountry[v.country].push(village);
            } else {
              this._villagesByCountry[v.country] = [village];
            }

            return village;
          }
        })
        .filter(Boolean);
    }
  }
}
